import * as d3 from "d3";
import {
  doSystemDateRetrieval,
  parseSystemDate,
  splitClusterResponse,
} from "common/api";

export function normalizeUnits(valueStr, units, precision) {
  var num = Number(valueStr);
  if (units == "Mbps" && num > 1000) {
    units = "Gbps";
    num /= 1000;
  }
  return num.toFixed(precision) + " " + units;
}

const normalizeUnitsMayBeUndefined = (value, ...args) =>
  value === undefined || value === null ? 'Unknown' :
  value === 'n/a' ? 'n/a' :
  normalizeUnits(value, ...args);

export const getAlarmsInfo = (bqns, bqnNr) => {
  return ifCl.run("show system alarms").then((response) => {
    const [header, ...rows] = response.trim(/\s/).split("\n");
    const headerAttrs = header.trim(/\s/).split(/\s+/);
    const headerIndexMap = headerAttrs.reduce((acc, value, index) => {
      acc[value] = index;
      return acc;
    }, {});

    const bqnsArray = bqns.length === 0 ? [{ bqnId: bqnNr }] : bqns;

    return bqnsArray.reduce((accBqns, { bqnId }) => {
      if (!accBqns[bqnId]) {
        const bqnAlarms = rows.reduce((acc, row) => {
          const rowAttrs = row.trim(/\s/).split(/\s+/);
          const type = rowAttrs[headerIndexMap["TYPE"]];
          const bqnIndex = rowAttrs[headerIndexMap["BQN"]];
          if (bqnId === bqnIndex || bqnId === parseFloat(bqnIndex)) {
            const rowInfo = rowAttrs.reduce((accRow, attr, index) => {
              accRow[headerAttrs[index]] = attr;
              return accRow;
            }, {});
            acc[type] = rowInfo;
          }
          return acc;
        }, {});
        accBqns[bqnId] = bqnAlarms;
      }
      return accBqns;
    }, {});
  });
};

// YYYY-MM-DD HH:mm:ss
export const timeFormat = d3.timeFormat("%Y-%m-%d %H:%M:%S");

export const getSystemDate = () => {
  return doSystemDateRetrieval().then((response) => {
    const attrs = response.trim(/\s/).split(/\s+/);
    const m = attrs[attrs.length - 1];
    const parseDate = timeFormat(parseSystemDate(response));
    return `${parseDate} ${m}`;
  });
};

export const getVersion = () => {
  return ifCl.run("show version").then((response) => {
    const bqnsResponses = splitClusterResponse(response);
    return bqnsResponses.reduce((acc, { bqnId, resp }) => {
      const valueRelease = resp.match(/BQN release: (.*)/);
      acc[bqnId] = valueRelease ? valueRelease[1] : "unknown";
      return acc;
    }, {});
  });
};

export const getSystemName = () => {
  return ifCl.run("show system hostname detail").then((response) => {
    const [header, ...rows] = response.trim(/\s/).split("\n");
    return rows.reduce((acc, value) => {
      const [bqn, hostname] = value.trim(/\s/).split(/\s+/);
      acc[bqn] = hostname || "Unknown";
      return acc;
    }, {});
  });
};

export const getHardwareId = () => {
  return ifCl.run("show hardware id").then((response) => {
    const [header, ...rows] = response.trim(/\s/).split("\n");
    return rows.reduce((acc, value) => {
      const [bqn, id] = value.trim(/\s/).split(/\s+/);
      acc[bqn] = id || "Unknown";
      return acc;
    }, {});
  });
};

export const getStartTime = (bqnId) => {
  return ifCl.run(`show process name ctrmgr 0 bqn-id ${bqnId}`).then((response) => {
    const valueTimeFound = response.match(/Time start: (.*)/);
    return valueTimeFound ? valueTimeFound[1].substring(0, 25) : "unknown";
  });
};

export const getSerialNumber = () => {
  return ifCl.run("show hardware chassis").then((response) => {
    const bqnsResponses = splitClusterResponse(response);
    return bqnsResponses.reduce((acc, { bqnId, resp }) => {
      const valueSerialRes = resp.match(/Serial: (.*)/);
      const valueSerial = valueSerialRes
        ? Number(valueSerialRes[1]) == 0
          ? "n/a"
          : valueSerialRes[1]
        : "n/a";
      acc[bqnId] = valueSerial;
      return acc;
    }, {});
  });
};

export const getLicense = (defaultBqnId=0) =>
  ifCl.run("show license").then((response) => {
    const [head, ...rows] = response.trim().split("\n");
    const headerAttrs = head.trim().split(/\s+/);
    const attrIndexMap = headerAttrs.reduce((acc, value, index) =>
      ({
        ...acc,
        [value]: index,
      }) , {});
    return rows.reduce((acc, row) => {
      const rowAttrs = row.trim().split(/\s+/);
      const feature = rowAttrs[attrIndexMap["FEATURE"]];
      const bqn = rowAttrs[attrIndexMap["BQN"]] || defaultBqnId;
      if (feature === undefined || feature.includes("bta") !== true) {
        acc[bqn] = acc[bqn] || ["n/a", "n/a"];
      } else {
        const usageMax = rowAttrs[attrIndexMap["USAGE-MAX"]];
        const expiration = rowAttrs[attrIndexMap["EXPIRATION"]];
        acc[bqn] = [usageMax, expiration.replace("T", " ")];
      }
      return acc;
    }, {});
  }).catch(console.error);

export const getSubscriberGroupStatus = (bqnId) => {
  return ifCl.run("show api subscriber-group status").then((response) => {
    const [header, ...rows] = response.trim(/\s/).split("\n");
    const headerAttrs = header.trim(/\s/).split(/\s+/);
    const attrIndexMap = headerAttrs.reduce((acc, value, index) => {
      acc[value] = index;
      return acc;
    }, {});

    return rows.reduce((acc, row) => {
      const rowAttrs = row.trim(/\s/).split(/\s+/);
      const bqn = rowAttrs[attrIndexMap["BQN"]];
      if (bqn === bqnId || bqnId === parseFloat(bqn)) {
        const active = rowAttrs[attrIndexMap["ACTIVE"]];
        acc = Number(active);
      }
      return acc;
    }, undefined);
  });
};

export const getSubscriberUsage = (bqnId) => {
  return ifCl
    .run(
      `show subscriber usage${bqnId !== undefined ? ` bqn-id ${bqnId}` : ""}`
    )
    .then((response) => {
      if(!response){
        return 0;
      }

      const rows = response.trim().split("\n");
      return rows.reduce((acc, row) => {
        const [key, value] = row.trim().split(/\s+/);
        if (key.startsWith("Active") && value !== undefined) {
          return value;
        }
        return acc;
      }, 0);
    });
};

export const getLatency = (bqnId) => {
  return ifCl.run(`show pkteng latency bqn-id ${bqnId}`).then((response) => {
    const [header, valuesRow] = response.trim().split("\n");
    const headerAttrs = header.trim().split(/\s+/);
    const { indexLatDown, indexLatUp } = headerAttrs.reduce(
      (acc, attr, index) => {
        if (attr === "RTT-MIN-DN-ms") {
          acc.indexLatDown = index;
        }
        if (attr === "RTT-MIN-UP-ms") {
          acc.indexLatUp = index;
        }
        return acc;
      },
      { indexLatDown: null, indexLatUp: null }
    );
    const values = valuesRow.trim().split(/\s+/);
    return {
      up: normalizeUnitsMayBeUndefined(values[indexLatUp], "ms", 1),
      down: normalizeUnitsMayBeUndefined(values[indexLatDown], "ms", 1),
    };
  });
};

export const getStatisticsPktEng = (bqnId) => {
  const cmd = `show pkteng statistics extended bqn-id ${bqnId}`;
  return ifCl.run(cmd).then((response) => {
    const rows = response.trim(/\s/).split("\n");
    return rows.reduce((acc, row) => {
      const [metric1, value1, metric2, value2] = row.trim("\n").split(/\s+/);
      if (metric1) {
        acc[metric1.slice(0, -1)] = parseFloat(value1);
      }
      if (metric2) {
        acc[metric2.slice(0, -1)] = parseFloat(value2);
      }
      return acc;
    }, {});
  });
};

export function percentOver(value, total) {
  if (value == 0 || total == 0) {
    return "0 %";
  } else {
    const ratio = value / total;
    return ((ratio > 1.0 ? 1.0 : ratio) * 100).toFixed(1) + " %";
  }
}

export function getDbTCPOpt(
  tcpDnTx,
  prevTcpDnTx,
  tcpUpTx,
  prevTcpUpTx,
  tcpOptDn,
  prevTcpOptDn,
  tcpOptUp,
  prevTcpOptUp
) {
  const tcpTotal = tcpDnTx - prevTcpDnTx + (tcpUpTx - prevTcpUpTx);
  if (tcpTotal > 0) {
    const percentage = (
      ((tcpOptDn - prevTcpOptDn + (tcpOptUp - prevTcpOptUp)) * 100) /
      tcpTotal
    ).toFixed(1);
    return `${percentage} %`;
  }
  return "0 %";
}

export function getdbPps(value, prevValue, elapsed) {
  let dbPpsValue = "n/a";
  let dbPpsUnit = "Kpps";
  if (value >= prevValue) {
    dbPpsValue = value - prevValue;
    if (elapsed > 0) {
      dbPpsValue = dbPpsValue / elapsed;
    }
    if (dbPpsValue >= 1000000) {
      dbPpsValue = (dbPpsValue / 1000000).toFixed(3);
      dbPpsUnit = "Mpps";
    } else if (dbPpsValue > 0) {
      dbPpsValue = (dbPpsValue / 1000).toFixed(3);
    }
  }
  return { dbPpsValue, dbPpsUnit };
}

export function formatLargeStrNum(valueStr, precision) {
  return Number(valueStr).toLocaleString("en-US", {
    maximumFractionDigits: precision,
  });
}
